import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PlanTier = "freemium" | "starter" | "colab";
export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | "incomplete" | "paused";

interface PlanFeature {
  enabled: boolean;
  limit: number | null;
}

interface PlanFeatures {
  [key: string]: PlanFeature;
}

interface UserPlanData {
  tier: PlanTier;
  planName: string;
  priceCents: number;
  status: SubscriptionStatus;
  trialStart: string | null;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  features: PlanFeatures;
}

const FALLBACK_PLAN: UserPlanData = {
  tier: "freemium",
  planName: "Básico",
  priceCents: 0,
  status: "active",
  trialStart: null,
  trialEnd: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  features: {},
};

export function useSubscription() {
  const { data, isLoading, error, refetch } = useQuery<UserPlanData>({
    queryKey: ["user-plan-features"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_user_plan_features");
      if (error) throw error;
      return (data as unknown as UserPlanData) ?? FALLBACK_PLAN;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const plan = data ?? FALLBACK_PLAN;

  const canAccess = (featureKey: string): boolean => {
    const feature = plan.features[featureKey];
    if (!feature) return false;
    return feature.enabled;
  };

  const getLimit = (featureKey: string): number | null => {
    const feature = plan.features[featureKey];
    if (!feature) return 0;
    if (!feature.enabled) return 0;
    return feature.limit;
  };

  const isWithinLimit = (featureKey: string, currentCount: number): boolean => {
    const feature = plan.features[featureKey];
    if (!feature) return false;
    if (!feature.enabled) return false;
    if (feature.limit === null) return true;
    return currentCount < feature.limit;
  };

  const openCheckout = async (tier: PlanTier) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { tier },
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const openCustomerPortal = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase.functions.invoke("create-portal-session");
    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const isFreemium = plan.tier === "freemium";
  const isPaid = plan.tier !== "freemium";
  const isTrial = plan.status === "trialing";
  const isActive = plan.status === "active" || plan.status === "trialing";
  const trialEndsAt = plan.trialEnd ? new Date(plan.trialEnd) : null;
  const currentPeriodEnd = plan.currentPeriodEnd ? new Date(plan.currentPeriodEnd) : null;
  const cancelAtPeriodEnd = plan.cancelAtPeriodEnd;

  return {
    plan,
    planTier: plan.tier,
    planName: plan.planName,
    status: plan.status,
    isFreemium,
    isPaid,
    isTrial,
    isActive,
    trialEndsAt,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    canAccess,
    getLimit,
    isWithinLimit,
    openCheckout,
    openCustomerPortal,
    isLoading,
    error,
    refetch,
  };
}
